import React, { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { formatDateOnly } from 'utils/formatting'
import useApiService from 'services/api'
import { LineItemDisplay } from '../LineItemDisplay'

const FULFILLMENT_OPTIONS: Record<string, string> = {
  pick_up_locally: 'Pick Up Locally',
  ship_to_address: 'Ship to Address',
  ship_with_existing_order: 'Ship with Existing Request',
}

export function PreviewStep() {
  const { watch } = useFormContext()
  const formData = watch()
  const api = useApiService()
  const { data: products } = api.useGetProducts()

  // Enrich line items with product/variant information
  const enrichedLineItems = useMemo(() => {
    if (!formData.line_items || !products) return []

    return formData.line_items.map((item: any) => {
      // Find product and variant info
      for (const product of products) {
        const variant = product.variants?.find((v) => v.id === item.variant_id)
        if (variant) {
          return {
            ...item,
            product_name: product.name,
            variant_name: variant.name,
            variant_sku: variant.sku,
          }
        }
      }
      return item
    })
  }, [formData.line_items, products])

  return (
    <div>
      <div className="p-4 bg-fb-green-50 border border-fb-green-200 rounded text-fb-green-900 mb-6">
        <strong>Review Your Request</strong>
        <div className="mt-2">
          Please review all information below. If you need to make changes, use
          the &quot;Back&quot; button to return to previous steps.
        </div>
      </div>

      <h3 className="text-lg font-semibold mb-3 text-gray-800 border-b-2 border-gray-200 pb-2">
        Request Information
      </h3>
      <div className="flex mb-2 text-[0.95rem]">
        <div className="font-semibold min-w-[180px] text-gray-700">
          Date Needed:
        </div>
        <div className="text-gray-800 flex-1">
          {formatDateOnly(formData.date_needed)}
        </div>
      </div>
      <div className="flex mb-2 text-[0.95rem]">
        <div className="font-semibold min-w-[180px] text-gray-700">
          Fulfillment Option:
        </div>
        <div className="text-gray-800 flex-1">
          {FULFILLMENT_OPTIONS[formData.fulfillment_option] ||
            formData.fulfillment_option}
        </div>
      </div>

      <h3 className="text-lg font-semibold mb-3 mt-6 text-gray-800 border-b-2 border-gray-200 pb-2">
        Contact Information
      </h3>
      <div className="flex mb-2 text-[0.95rem]">
        <div className="font-semibold min-w-[180px] text-gray-700">
          Contact Name:
        </div>
        <div className="text-gray-800 flex-1">
          {formData.contact_first_name} {formData.contact_last_name}
        </div>
      </div>
      <div className="flex mb-2 text-[0.95rem]">
        <div className="font-semibold min-w-[180px] text-gray-700">
          Contact Email:
        </div>
        <div className="text-gray-800 flex-1">{formData.contact_email}</div>
      </div>
      {formData.contact_phone && (
        <div className="flex mb-2 text-[0.95rem]">
          <div className="font-semibold min-w-[180px] text-gray-700">
            Contact Phone:
          </div>
          <div className="text-gray-800 flex-1">{formData.contact_phone}</div>
        </div>
      )}

      {formData.fulfillment_option === 'ship_to_address' && (
        <>
          <h3 className="text-lg font-semibold mb-3 mt-6 text-gray-800 border-b-2 border-gray-200 pb-2">
            Shipping Address
          </h3>
          <div className="flex mb-2 text-[0.95rem]">
            <div className="font-semibold min-w-[180px] text-gray-700">
              Address Line 1:
            </div>
            <div className="text-gray-800 flex-1">
              {formData.shipping_address_line1}
            </div>
          </div>
          {formData.shipping_address_line2 && (
            <div className="flex mb-2 text-[0.95rem]">
              <div className="font-semibold min-w-[180px] text-gray-700">
                Address Line 2:
              </div>
              <div className="text-gray-800 flex-1">
                {formData.shipping_address_line2}
              </div>
            </div>
          )}
          <div className="flex mb-2 text-[0.95rem]">
            <div className="font-semibold min-w-[180px] text-gray-700">
              City:
            </div>
            <div className="text-gray-800 flex-1">{formData.shipping_city}</div>
          </div>
          <div className="flex mb-2 text-[0.95rem]">
            <div className="font-semibold min-w-[180px] text-gray-700">
              State:
            </div>
            <div className="text-gray-800 flex-1">
              {formData.shipping_state}
            </div>
          </div>
          <div className="flex mb-2 text-[0.95rem]">
            <div className="font-semibold min-w-[180px] text-gray-700">
              ZIP Code:
            </div>
            <div className="text-gray-800 flex-1">{formData.shipping_zip}</div>
          </div>
          <div className="flex mb-2 text-[0.95rem]">
            <div className="font-semibold min-w-[180px] text-gray-700">
              Country:
            </div>
            <div className="text-gray-800 flex-1">
              {formData.shipping_country || 'US'}
            </div>
          </div>
        </>
      )}

      <h3 className="text-lg font-semibold mb-3 mt-6 text-gray-800 border-b-2 border-gray-200 pb-2">
        Products
      </h3>
      {enrichedLineItems && enrichedLineItems.length > 0 ? (
        enrichedLineItems.map((item: any, index: number) => (
          <LineItemDisplay key={index} item={item} />
        ))
      ) : (
        <div className="text-gray-500">No products selected</div>
      )}

      {formData.special_instructions && (
        <>
          <h3 className="text-lg font-semibold mb-3 mt-6 text-gray-800 border-b-2 border-gray-200 pb-2">
            Special Instructions
          </h3>
          <div className="p-3 bg-gray-50 rounded whitespace-pre-wrap">
            {formData.special_instructions}
          </div>
        </>
      )}
    </div>
  )
}
